import React, { useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import isDifferent from 'deep-diff';
import { useForm } from 'react-hook-form';
import { useQuery, usePreventWindowUnload } from '@panel/commons/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers';
import { Step1Module1Selectors } from '../../../ducks/step1Module1';
import { Step3Module1Selectors, Step3Module1Operations } from '../../../ducks/step3';
import { SavePostingOperations } from '../../../ducks/savePosting';
import { WrapperContainer } from '../../Wrappers/WrapperContainer';
import { AmenitiesSection } from './amenities';
import { AdditionalSection } from './additional';
import { sendEvent, PUBLICATION_ABANDON, track } from '../../../utils/trackingEvent';
import { step3BodyManager } from '../../../utils/bodyManager';
import { getYupSchema } from '../validations';
import { updateLastStep } from '../../../ducks/stepper';
import { useStepperCtx } from '../../stepper';

const SectionsWrapper = WrapperContainer(({ setId, loading }) => {
  const { features, featureCategories, featuresOriginal, textInputsValues } = useSelector(
    Step3Module1Selectors.getStep3State
  );
  const {
    postingStatuses,
    operationTypeId,
    propertyTypeId,
    subPropertyTypeId,
    propertyTypes,
    propertySubTypes
  } = useSelector(Step1Module1Selectors.getInfoEvents);
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const { setPreventTransition, nextStepperRoute } = useStepperCtx();
  const { push } = useHistory();
  const dispatch = useDispatch();

  const defaultValues = textInputsValues;
  const isDirty = isDifferent(features, featuresOriginal, (path, key) => key === 'touched') !== undefined;
  const { register, handleSubmit, reset, errors, formState } = useForm({
    defaultValues,
    resolver: yupResolver(getYupSchema(featuresOriginal.featureInputs)),
    mode: 'onBlur',
    shouldFocusError: true
  });
  const formRef = useRef(null);

  useEffect(() => {
    setId('step3');
  }, [setId]);

  const notifyAbandon = () => {
    if (postingStatuses.includes('DRAFT')) {
      track(PUBLICATION_ABANDON, operationTypeId, postingId);
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', notifyAbandon);
    return () => {
      window.removeEventListener('beforeunload', notifyAbandon);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingStatuses]);

  useEffect(() => {
    dispatch(Step3Module1Operations.getFeatures(postingId));
  }, [dispatch, postingId]);

  useEffect(() => {
    reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuresOriginal, reset]);

  useEffect(() => {
    if (isDirty || formState.isDirty) {
      setPreventTransition(formRef);
    } else {
      setPreventTransition(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, formState.isDirty]);

  const onSubmit = (data, e) => {
    const payload = {
      body: step3BodyManager(features, featuresOriginal, postingId, data),
      step: 'STEP_EXTRA'
    };
    const { nativeEvent } = e;
    const saveAndExit = nativeEvent.submitter && nativeEvent.submitter.id === 'saveAndExit';
    const savedFromModal = nativeEvent.detail && nativeEvent.detail.submitter === 'saveAndRedirect';
    const goBack = nativeEvent.detail && nativeEvent.detail.goBack;
    const path = goBack || nextStepperRoute || '/publicador/planes/seleccion';

    const propertyObj = { list: propertyTypes, id: propertyTypeId };
    const subPropertyObj = { list: propertySubTypes, id: subPropertyTypeId };

    sendEvent('Extras', saveAndExit ? 'Guardar y salir' : 'Continuar', operationTypeId, propertyObj, subPropertyObj);
    if (saveAndExit) {
      notifyAbandon();
    }
    if (payload.body.features.length > 0) {
      dispatch(SavePostingOperations.savePostingDB(payload, path, saveAndExit, savedFromModal));
    } else {
      const next = !saveAndExit
        ? {
            pathname: '/publicador/planes/seleccion',
            search: `?postingId=${postingId}`
          }
        : {
            pathname: '/bum'
          };
      push(next);
    }
    dispatch(updateLastStep('3', '1'));
  };

  usePreventWindowUnload(isDirty || formState.isDirty);

  return (
    <form id='step3' data-repa='step3-section' ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <fieldset data-repa='step3-fieldset' disabled={loading}>
        <AmenitiesSection features={features} featureCategories={featureCategories} />
        <AdditionalSection errors={errors} register={register} features={features} />
      </fieldset>
    </form>
  );
});

export { SectionsWrapper };
